import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Switch, Table, Tag } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { getRequest, postRequest, patchRequest } from 'api';
import { toast } from 'react-toastify';

const { Option } = Select;

interface IncomeSource {
    id: number;
    name: string;
    type: number;
    code?: string;
    collectBy?: number;
    isEinvoice?: boolean;
    isOptional?: boolean;
}

const RevenueSourceList: React.FC = () => {
    const [typeFilter, setTypeFilter] = useState<number | null>(null);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const [data, setData] = useState<IncomeSource[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState<IncomeSource | null>(null);

    const getTypeName = (type: number) => {
        switch (type) {
            case 1: return 'Nhóm ăn uống';
            case 2: return 'Nhóm học phí';
            case 3: return 'Nhóm học theo buổi (tiết)';
            case 4: return 'Nhóm chi phí khác';
            case 5: return 'Nhóm thu hộ';
            default: return 'Không xác định';
        }
    };

    const getCollectByName = (collectBy?: number) => {
        switch (collectBy) {
            case 1: return 'Theo tháng';
            case 2: return 'Theo năm';
            case 3: return 'Suất-tháng';
            case 4: return 'Theo kỳ';
            default: return 'Theo tháng';
        }
    };

    const getCollectByTagColor = (collectBy?: number) => {
        switch (collectBy) {
            case 1: return 'blue';
            case 2: return 'purple';
            case 3: return 'orange';
            case 4: return 'cyan';
            default: return 'blue';
        }
    };

    const fetchData = async () => {
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId) {
            toast.error('Không tìm thấy schoolId');
            return;
        }

        setLoading(true);
        try {
            const response = await getRequest(`/cms/accountant/income-school?schoolId=${schoolId}&page=1&perPage=100`);
            if (response?.code === 200 && Array.isArray(response.info?.list)) {
                const sorted = [...response.info.list].sort((a, b) => a.type - b.type);
                setData(sorted);
            } else {
                toast.error('Không thể tải danh sách nguồn thu');
            }
        } catch (error) {
            toast.error('Lỗi khi tải danh sách nguồn thu');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCreate = async (values: any) => {
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId) {
            toast.error('Không tìm thấy schoolId');
            return;
        }

        const response = await postRequest(`/cms/accountant/income-school?schoolId=${schoolId}`, {
            name: values.name,
            type: values.type,
            code: values.code ? values.code.trim().toUpperCase() : undefined,
            collectBy: values.collectBy || 1,
            isEinvoice: !!values.isEinvoice,
            isOptional: !!values.isOptional,
        });

        if (response?.code === 200) {
            toast.success('Tạo nguồn thu thành công');
            setModalVisible(false);
            form.resetFields();
            fetchData();
        } else {
            toast.error(response?.msg || 'Tạo nguồn thu thất bại');
        }
    };

    const handleEdit = async (values: any) => {
        if (!selectedItem) return;

        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId) {
            toast.error('Không tìm thấy schoolId');
            return;
        }

        const response = await patchRequest(`/cms/accountant/income-school?schoolId=${schoolId}`, {
            id: selectedItem.id,
            name: values.name,
            type: selectedItem.type,
            code: values.code ? values.code.trim().toUpperCase() : undefined,
            collectBy: values.collectBy || 1,
            isEinvoice: !!values.isEinvoice,
            isOptional: !!values.isOptional,
        });

        if (response?.code === 200) {
            toast.success('Cập nhật thành công');
            setEditModalVisible(false);
            setSelectedItem(null);
            fetchData();
        } else {
            toast.error(response?.msg || 'Cập nhật thất bại');
        }
    };

    const columns = [
        {
            title: 'Mã khoản',
            dataIndex: 'code',
            key: 'code',
            width: 120,
            render: (code?: string) => code ? <Tag color="blue" style={{ fontWeight: 600 }}>{code}</Tag> : <span style={{ color: '#ccc' }}>-</span>,
        },
        {
            title: 'Loại nguồn thu',
            dataIndex: 'type',
            key: 'type',
            width: 170,
            render: (type: number) => getTypeName(type),
            sorter: (a: IncomeSource, b: IncomeSource) => a.type - b.type,
        },
        {
            title: 'Tên nguồn thu',
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => <span style={{ fontWeight: 500 }}>{name}</span>,
        },
        {
            title: 'Cách thu',
            dataIndex: 'collectBy',
            key: 'collectBy',
            width: 130,
            align: 'center' as const,
            render: (collectBy?: number) => (
                <Tag color={getCollectByTagColor(collectBy)}>
                    {getCollectByName(collectBy)}
                </Tag>
            ),
        },
        {
            title: 'HĐĐT',
            dataIndex: 'isEinvoice',
            key: 'isEinvoice',
            width: 100,
            align: 'center' as const,
            render: (isEinvoice?: boolean) => (
                <Tag color={isEinvoice ? 'green' : 'default'}>
                    {isEinvoice ? 'Xuất HĐ' : 'Không'}
                </Tag>
            ),
        },
        {
            title: 'Tự chọn',
            dataIndex: 'isOptional',
            key: 'isOptional',
            width: 100,
            align: 'center' as const,
            render: (isOptional?: boolean) => (
                <Tag color={isOptional ? 'geekblue' : 'default'}>
                    {isOptional ? 'Tự chọn' : 'Bắt buộc'}
                </Tag>
            ),
        },
        {
            title: 'Hành động',
            key: 'action',
            width: 110,
            align: 'center' as const,
            render: (_: any, record: IncomeSource) => (
                <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => {
                        setSelectedItem(record);
                        editForm.setFieldsValue({
                            name: record.name,
                            code: record.code,
                            collectBy: record.collectBy || 1,
                            isEinvoice: !!record.isEinvoice,
                            isOptional: !!record.isOptional,
                        });
                        setEditModalVisible(true);
                    }}
                >
                    Cập nhật
                </Button>
            ),
        },
    ];

    const filteredData = typeFilter ? data.filter(item => item.type === typeFilter) : data;

    return (
        <div style={{ padding: '4px 0' }}>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            form.resetFields();
                            form.setFieldsValue({ collectBy: 1, isEinvoice: false, isOptional: false });
                            setModalVisible(true);
                        }}
                    >
                        Thêm nguồn thu
                    </Button>
                    <Select
                        placeholder="Lọc theo nhóm thu"
                        value={typeFilter}
                        onChange={(value) => setTypeFilter(value)}
                        allowClear
                        style={{ width: 220 }}
                    >
                        <Select.Option value={1}>1: Nhóm ăn uống</Select.Option>
                        <Select.Option value={2}>2: Nhóm học phí</Select.Option>
                        <Select.Option value={3}>3: Nhóm học theo buổi (tiết)</Select.Option>
                        <Select.Option value={4}>4: Nhóm chi phí khác</Select.Option>
                        <Select.Option value={5}>5: Nhóm thu hộ</Select.Option>
                    </Select>
                </Space>
                <div style={{ color: '#888', fontSize: 13 }}>
                    * Cách thu: "Suất-tháng" dành cho Tiền ăn, Phục vụ bán trú (có chấm ngày và chốt thực tế)
                </div>
            </div>

            <Table
                rowKey="id"
                dataSource={filteredData}
                columns={columns}
                loading={loading}
                pagination={false}
                bordered
            />

            {/* Modal thêm mới */}
            <Modal
                title="Thêm nguồn thu mới"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={() => form.submit()}
                okText="Tạo nguồn thu"
                cancelText="Hủy"
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleCreate}>
                    <Form.Item
                        label="Loại nguồn thu"
                        name="type"
                        rules={[{ required: true, message: 'Vui lòng chọn loại nguồn thu' }]}
                    >
                        <Select placeholder="Chọn loại nguồn thu">
                            <Option value={1}>1: Nhóm ăn uống</Option>
                            <Option value={2}>2: Nhóm học phí</Option>
                            <Option value={3}>3: Nhóm học theo buổi (tiết)</Option>
                            <Option value={4}>4: Nhóm chi phí khác</Option>
                            <Option value={5}>5: Nhóm thu hộ</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="Mã nguồn thu"
                        name="code"
                        rules={[{ max: 50, message: 'Mã tối đa 50 ký tự' }]}
                    >
                        <Input placeholder="Ví dụ: TIEN_AN, HP, PV_BAN_TRU..." style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                    <Form.Item
                        label="Tên nguồn thu"
                        name="name"
                        rules={[{ required: true, message: 'Vui lòng nhập tên nguồn thu' }]}
                    >
                        <Input placeholder="Ví dụ: Tiền ăn tháng, Học phí chính khóa..." />
                    </Form.Item>
                    <Form.Item
                        label="Cách thu"
                        name="collectBy"
                        initialValue={1}
                        rules={[{ required: true, message: 'Vui lòng chọn cách thu' }]}
                    >
                        <Select>
                            <Option value={1}>1: Thu theo tháng</Option>
                            <Option value={2}>2: Thu theo năm</Option>
                            <Option value={3}>3: Suất-tháng (Có chấm ngày & chốt thực tế)</Option>
                            <Option value={4}>4: Thu theo kỳ</Option>
                        </Select>
                    </Form.Item>
                    <Space size="large" style={{ marginTop: 8 }}>
                        <Form.Item label="Xuất HĐĐT" name="isEinvoice" valuePropName="checked" initialValue={false}>
                            <Switch />
                        </Form.Item>
                        <Form.Item label="Khoản tự chọn" name="isOptional" valuePropName="checked" initialValue={false}>
                            <Switch />
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>

            {/* Modal chỉnh sửa */}
            <Modal
                title="Chỉnh sửa nguồn thu"
                open={editModalVisible}
                onCancel={() => {
                    setEditModalVisible(false);
                    setSelectedItem(null);
                }}
                onOk={() => editForm.submit()}
                okText="Lưu"
                cancelText="Hủy"
                destroyOnClose
            >
                <Form form={editForm} layout="vertical" onFinish={handleEdit}>
                    <Form.Item
                        label="Mã nguồn thu"
                        name="code"
                        rules={[{ max: 50, message: 'Mã tối đa 50 ký tự' }]}
                    >
                        <Input placeholder="Ví dụ: TIEN_AN, HP..." style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                    <Form.Item
                        label="Tên nguồn thu"
                        name="name"
                        rules={[{ required: true, message: 'Vui lòng nhập tên nguồn thu' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Cách thu"
                        name="collectBy"
                        rules={[{ required: true, message: 'Vui lòng chọn cách thu' }]}
                    >
                        <Select>
                            <Option value={1}>1: Thu theo tháng</Option>
                            <Option value={2}>2: Thu theo năm</Option>
                            <Option value={3}>3: Suất-tháng (Có chấm ngày & chốt thực tế)</Option>
                            <Option value={4}>4: Thu theo kỳ</Option>
                        </Select>
                    </Form.Item>
                    <Space size="large" style={{ marginTop: 8 }}>
                        <Form.Item label="Xuất HĐĐT" name="isEinvoice" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                        <Form.Item label="Khoản tự chọn" name="isOptional" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>
        </div>
    );
};

export default RevenueSourceList;
